import React, {useState, useMemo} from 'react';
import {ScrollView, StyleSheet, Pressable, Linking} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import CCTextInput from '@/Atoms/CCTextInput';
import CCColors from '@/Utils/CCColors';
import {FontSizes, vs, hs} from '@/Utils/Dimensions';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import {useNavigation, useRoute} from '@react-navigation/native';
import {ScreenName} from '@/navigators/StackConstants';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import {formatCurrency} from '@/Utils/CommonUtils';
import useCreateRenewalOrder from '@/Hooks/RenewalApi/useCreateRenewalOrder';
import CCLoader from '@/Atoms/CCLoader';
import Spacer from '@/Atoms/Spacer';
import CouponModal from './CouponModal';

interface RenewalPaymentScreenProps {}

const RenewalPaymentScreen: React.FC<RenewalPaymentScreenProps> = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const {isMobileOrTab} = useMediaQuery();
  const [couponCode, setCouponCode] = useState<string>('');
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  const [isCheckingCoupon, setIsCheckingCoupon] = useState(false);
  const [showCouponModal, setShowCouponModal] = useState(false);

  const {
    renewalData,
    selectedPlanType,
    selectedDuration,
    selectedPricing,
    selectedSlots = [],
    totalSessions,
  } = route.params || {};

  const {mutate: createRenewalOrder, isLoading: isCreatingOrder} = useCreateRenewalOrder(
    (data) => {
      // Handle successful payment link generation
      if (data?.data?.payment_link) {
        Linking.openURL(data.data.payment_link);
      }
    },
    (error) => {
      console.error('Error creating renewal order:', error);
    },
  );

  // Calculate pricing breakdown using new price_per_session structure
  const pricingBreakdown = useMemo(() => {
    if (!selectedPricing || !selectedPricing.price_per_session) return null;

    const pricePerSession = parseFloat(selectedPricing.price_per_session || selectedPricing.per_session_price || '0');
    const currency = selectedPricing.currency_code || selectedPricing.currency || 'INR';
    const gstRate = 0.18; // 18% GST
    
    // Calculate sessions per month: 3 months = 24 sessions, so 8 sessions per month
    const sessionsPerMonth = 8;
    const totalSessionsForDuration = sessionsPerMonth * selectedDuration;
    
    // Base price = price per session * total sessions
    // Note: price_per_session from backend already includes GST, so no need to add GST again
    const basePriceExclGst = pricePerSession * totalSessionsForDuration;
    
    // Price already includes GST, so basePrice = basePriceExclGst (no additional GST)
    // Calculate GST amount for display purposes (GST is already included in the price)
    // GST = Price × (GST Rate / (1 + GST Rate))
    const gstAmount = currency === 'USD' ? 0 : basePriceExclGst * (gstRate / (1 + gstRate));
    const basePrice = basePriceExclGst; // Already includes GST
    
    // Early renewal discount (not available in new structure, set to 0)
    const earlyRenewalDiscountPct = 0;
    const earlyRenewalDiscount = 0;

    // Calculate coupon discount
    let couponDiscount = 0;
    if (appliedCoupon) {
      if (appliedCoupon.discount_type === 'percentage') {
        couponDiscount = (basePrice * parseFloat(appliedCoupon.discount_value)) / 100;
      } else {
        couponDiscount = parseFloat(appliedCoupon.discount_value);
      }
    }

    // Calculate subtotal after discounts
    const subtotal = basePrice - earlyRenewalDiscount - couponDiscount;

    // Grand total
    const grandTotal = subtotal;

    // Monthly equivalent
    const monthlyEquivalent = grandTotal / selectedDuration;

    return {
      basePrice,
      earlyRenewalDiscount,
      earlyRenewalDiscountPct,
      couponDiscount,
      subtotal,
      gstAmount,
      grandTotal,
      monthlyEquivalent,
      currency,
    };
  }, [selectedPricing, appliedCoupon, selectedDuration, selectedPlanType]);

  // Auto-apply coupon from renewal data
  React.useEffect(() => {
    if (renewalData?.coupon && !appliedCoupon) {
      setAppliedCoupon(renewalData.coupon);
      setCouponCode(renewalData.coupon.code);
    }
  }, [renewalData]);

  const handleApplyCoupon = (coupon: any) => {
    setAppliedCoupon(coupon);
    setCouponCode(coupon.code);
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  const handlePayNow = () => {
    if (!renewalData || !selectedPricing) return;

    const user = renewalData.user;
    const returnUrl = `${window.location.origin}/renewal/success`;

    createRenewalOrder({
      tournament_id: renewalData.recommended_next_level?.tournament_id || renewalData.current_subscription.tournament_id,
      plan_type: selectedPlanType,
      duration_months: selectedDuration,
      coupon_code: appliedCoupon?.code,
      selectedSlots: selectedSlots,
      customer_id: user.id,
      mobile_number: user.mobile,
      email: user.email || `${user.mobile}@circlechess.com`,
      return_url: returnUrl,
    });
  };


  if (!renewalData || !selectedPricing || !pricingBreakdown) {
    return (
      <Box flex={1} justifyContent="center" alignItems="center">
        <CCText>Loading...</CCText>
      </Box>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      {/* Header */}
      <Box flexDirection="row" justifyContent="space-between" alignItems="center" padding={20}>
        <Box flexDirection="row" alignItems="center" gap={10}>
          <CCText fontSize={FontSizes[24]} fontWeight="bold" color={CCColors.TextColor.Dark}>
            CircleChess
          </CCText>
        </Box>
        <Pressable onPress={() => navigation.goBack()}>
          <MaterialCommunityIcons name="phone" size={24} color={CCColors.TextColor.Dark} />
        </Pressable>
      </Box>

      <Box paddingHorizontal={20}>
        <CCText fontSize={FontSizes[28]} fontWeight="bold" color={CCColors.TextColor.Dark} marginBottom={30}>
          Pay Now
        </CCText>

        {/* Coupon Code Section */}
        <Box backgroundColor={CCColors.White} padding={20} borderRadius={8} marginBottom={20}>
          <Box flexDirection="row" justifyContent="space-between" alignItems="center" marginBottom={10}>
            <CCText fontSize={FontSizes[16]} fontWeight="semibold">
              Coupon Code
            </CCText>
            <Pressable onPress={() => setShowCouponModal(true)}>
              <Box flexDirection="row" alignItems="center" gap={6}>
                <MaterialCommunityIcons name="ticket-percent" size={16} color={CCColors.Primary.Default} />
                <CCText fontSize={FontSizes[14]} color={CCColors.Primary.Default} fontWeight="semibold">
                  View All Coupons
                </CCText>
              </Box>
            </Pressable>
          </Box>
          {appliedCoupon ? (
            <Box
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              padding={12}
              backgroundColor={CCColors.Primary.lightBrown}
              borderRadius={8}
              borderWidth={1}
              borderColor={CCColors.Green}>
              <Box flexDirection="row" alignItems="center" gap={8}>
                <MaterialCommunityIcons name="check-circle" size={20} color={CCColors.Green} />
                <Box>
                  <CCText fontSize={FontSizes[14]} fontWeight="semibold" color={CCColors.TextColor.Dark}>
                    {appliedCoupon.code}
                  </CCText>
                  <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Grey}>
                    {appliedCoupon.discount_type === 'percentage' 
                      ? `${appliedCoupon.discount_value}% off`
                      : `${formatCurrency(parseFloat(appliedCoupon.discount_value), appliedCoupon.currency || 'INR')} off`}
                  </CCText>
                </Box>
              </Box>
              <Pressable onPress={handleRemoveCoupon}>
                <MaterialCommunityIcons name="close-circle" size={20} color={CCColors.Red} />
              </Pressable>
            </Box>
          ) : (
            <Box flexDirection="row" gap={10}>
              <Box flex={1}>
                <CCTextInput
                  placeholder="Enter code"
                  value={couponCode}
                  onChangeText={setCouponCode}
                  style={styles.couponInput}
                />
              </Box>
              <CCButton
                onPress={() => setShowCouponModal(true)}
                style={styles.checkButton}
                contentContainerStyle={styles.checkButtonContent}>
                <MaterialCommunityIcons name="ticket-percent" size={16} color={CCColors.TextColor.Dark} />
              </CCButton>
            </Box>
          )}
        </Box>

        {/* Bill Details */}
        <Box backgroundColor={CCColors.White} padding={20} borderRadius={8} marginBottom={20}>
          <CCText fontSize={FontSizes[16]} fontWeight="semibold" marginBottom={15}>
            Bill Details
          </CCText>

          <Box flexDirection="row" justifyContent="space-between" marginBottom={10}>
            <Box flex={1}>
              <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Dark}>
                Base Fee
              </CCText>
              <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Grey}>
                ({selectedDuration} Month Plan)
              </CCText>
            </Box>
            <CCText fontSize={FontSizes[14]} fontWeight="semibold" color={CCColors.TextColor.Dark}>
              {formatCurrency(pricingBreakdown.basePrice, pricingBreakdown.currency)}
            </CCText>
          </Box>

          {pricingBreakdown.earlyRenewalDiscount > 0 && (
            <Box flexDirection="row" justifyContent="space-between" marginBottom={10}>
              <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Dark}>
                Early renewal discount
              </CCText>
              <CCText fontSize={FontSizes[14]} fontWeight="semibold" color={CCColors.Green}>
                -{formatCurrency(pricingBreakdown.earlyRenewalDiscount, pricingBreakdown.currency)}
              </CCText>
            </Box>
          )}

          {pricingBreakdown.couponDiscount > 0 && (
            <Box flexDirection="row" justifyContent="space-between" marginBottom={10}>
              <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Dark}>
                Coupon discount
              </CCText>
              <CCText fontSize={FontSizes[14]} fontWeight="semibold" color={CCColors.Green}>
                -{formatCurrency(pricingBreakdown.couponDiscount, pricingBreakdown.currency)}
              </CCText>
            </Box>
          )}

          <Box height={1} backgroundColor={CCColors.BorderBottom} marginVertical={15} />

          <Box flexDirection="row" justifyContent="space-between" marginBottom={10}>
            <Box flex={1}>
              <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Dark}>
                GST (18%)
              </CCText>
            </Box>
            <CCText fontSize={FontSizes[14]} fontWeight="semibold" color={CCColors.TextColor.Dark}>
              {formatCurrency(pricingBreakdown.gstAmount, pricingBreakdown.currency)}
            </CCText>
          </Box>

          <Box height={1} backgroundColor={CCColors.BorderBottom} marginVertical={15} />

          <Box flexDirection="row" justifyContent="space-between">
            <Box flex={1}>
              <CCText fontSize={FontSizes[16]} fontWeight="bold" color={CCColors.TextColor.Dark}>
                Grand Total
              </CCText>
              <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Grey}>
                ({formatCurrency(pricingBreakdown.monthlyEquivalent, pricingBreakdown.currency)} Monthly)
              </CCText>
            </Box>
            <CCText fontSize={FontSizes[18]} fontWeight="bold" color={CCColors.TextColor.Dark}>
              {formatCurrency(pricingBreakdown.grandTotal, pricingBreakdown.currency)}
            </CCText>
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        padding={20}
        backgroundColor={CCColors.TextColor.Dark}
        style={{marginTop: 'auto'}}>
        <Box>
          <CCText fontSize={FontSizes[16]} fontWeight="semibold" color={CCColors.White}>
            {selectedPlanType === 'personal' ? 'Personal 1:1' : 'Small Group'}
          </CCText>
          <CCText fontSize={FontSizes[12]} color={CCColors.White}>
            {selectedPlanType === 'personal' ? 'One-on-one' : '4-6 Students'} | {selectedDuration} Months
          </CCText>
        </Box>
        <CCButton
          onPress={handlePayNow}
          disabled={isCreatingOrder}
          style={styles.payButton}
          contentContainerStyle={styles.payButtonContent}>
          {isCreatingOrder ? (
            <CCLoader loading={true} />
          ) : (
            <CCText fontSize={FontSizes[16]} fontWeight="semibold" color={CCColors.TextColor.Dark}>
              Pay {formatCurrency(pricingBreakdown.basePrice, pricingBreakdown.currency)}
            </CCText>
          )}
        </CCButton>
      </Box>

      {/* Coupon Modal */}
      <CouponModal
        visible={showCouponModal}
        onClose={() => setShowCouponModal(false)}
        onApplyCoupon={handleApplyCoupon}
        tournamentId={renewalData.recommended_next_level?.tournament_id || renewalData.current_subscription.tournament_id}
        planType={selectedPlanType}
        durationMonths={selectedDuration}
        appliedCouponCode={appliedCoupon?.code}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: CCColors.CCTableBg,
  },
  contentContainer: {
    paddingBottom: 100,
  },
  couponInput: {
    flex: 1,
  },
  checkButton: {
    backgroundColor: CCColors.LightYellow,
    borderRadius: 8,
    paddingHorizontal: 20,
  },
  checkButtonContent: {
    padding: 0,
  },
  payButton: {
    backgroundColor: CCColors.LightYellow,
    borderRadius: 8,
    paddingHorizontal: 30,
    paddingVertical: 12,
  },
  payButtonContent: {
    padding: 0,
  },
});

export default RenewalPaymentScreen;

